import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, Clock, User, Car, Phone } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Modal from '../components/Modal';
import { Input, Select, FormRow } from '../components/FormComponents';
import Breadcrumb from '../components/Breadcrumb';
import Badge from '../components/Badge';
import { getMonthName } from '../utils/helpers';

const EVENT_TYPES = [
  { value: 'testdrive', label: 'Test Drive', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'followup', label: 'Follow-up', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'delivery', label: 'Delivery', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'meeting', label: 'Meeting', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'other', label: 'Lainnya', color: 'bg-gray-100 text-gray-700 border-gray-200' },
];

const emptyForm = { title: '', date: '', time: '10:00', type: 'followup', customerId: '' };

export default function Calendar() {
  const { state, dispatch, showNotification } = useApp();
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const getEventsForDate = (d) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return state.events.filter(e => e.date === dateStr);
  };

  const getEventColor = (type) => EVENT_TYPES.find(t => t.value === type)?.color || 'bg-gray-100 text-gray-700';

  const handleAddEvent = () => {
    if (!form.title || !form.date) return showNotification('Judul dan tanggal wajib diisi', 'error');
    dispatch({ type: 'ADD_EVENT', payload: form });
    showNotification('Event berhasil ditambahkan');
    setShowModal(false);
    setForm(emptyForm);
  };

  const openAddForDate = (d) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    setForm({ ...emptyForm, date: dateStr });
    setShowModal(true);
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  // Upcoming events
  const todayStr = today.toISOString().split('T')[0];
  const upcoming = [...state.events].filter(e => e.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 5);

  // Build calendar grid
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: daysInPrevMonth - i, current: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, current: true });
  while (cells.length % 7 !== 0) cells.push({ day: cells.length - daysInMonth - firstDay + 2, current: false });

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Kalender' }]} />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Kalender Kegiatan</h1>
        <button onClick={() => { setForm(emptyForm); setShowModal(true); }} className="btn-primary"><Plus size={16}/>Tambah Event</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 card">
          {/* Nav */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-gray-100 transition-colors"><ChevronLeft size={18} /></button>
            <h2 className="text-base font-bold text-gray-800">{getMonthName(month)} {year}</h2>
            <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-gray-100 transition-colors"><ChevronRight size={18} /></button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map(d => (
              <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2">{d}</div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, i) => {
              const events = cell.current ? getEventsForDate(cell.day) : [];
              const isToday = cell.current && cell.day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              const isSelected = cell.current && selectedDate === cell.day;
              return (
                <div
                  key={i}
                  onClick={() => cell.current && setSelectedDate(cell.day === selectedDate ? null : cell.day)}
                  className={`min-h-16 p-1 rounded-lg cursor-pointer transition-colors border ${
                    isSelected ? 'border-red-400 bg-red-50' :
                    isToday ? 'border-red-200 bg-red-50/50' :
                    cell.current ? 'border-transparent hover:bg-gray-50' : 'border-transparent opacity-40 cursor-default'
                  }`}
                >
                  <span className={`text-xs font-medium block mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-red-600 text-white' : cell.current ? 'text-gray-700' : 'text-gray-400'}`}>
                    {cell.day}
                  </span>
                  <div className="space-y-0.5">
                    {events.slice(0, 2).map(ev => (
                      <div key={ev.id} className={`text-xs px-1 py-0.5 rounded truncate border ${getEventColor(ev.type)}`}>{ev.title}</div>
                    ))}
                    {events.length > 2 && <div className="text-xs text-gray-400">+{events.length - 2}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Selected Date Events */}
          {selectedDate && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {selectedDate} {getMonthName(month)} {year}
              </h3>
              {selectedDateEvents.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-xs text-gray-400 mb-2">Tidak ada event</p>
                  <button onClick={() => openAddForDate(selectedDate)} className="btn-primary text-xs py-1.5"><Plus size={12}/>Tambah</button>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedDateEvents.map(ev => (
                    <div key={ev.id} className={`p-2 rounded-lg border ${getEventColor(ev.type)} flex items-start justify-between gap-2`}>
                      <div>
                        <p className="text-xs font-medium">{ev.title}</p>
                        {ev.time && <p className="text-xs opacity-70 flex items-center gap-1 mt-0.5"><Clock size={10}/>{ev.time}</p>}
                      </div>
                      <button onClick={() => { dispatch({ type: 'DELETE_EVENT', payload: ev.id }); showNotification('Event dihapus'); }} className="hover:opacity-70">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upcoming */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Agenda Mendatang</h3>
            {upcoming.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">Tidak ada agenda</p>
            ) : (
              <div className="space-y-2">
                {upcoming.map(ev => (
                  <div key={ev.id} className={`p-2 rounded-lg border ${getEventColor(ev.type)}`}>
                    <p className="text-xs font-medium">{ev.title}</p>
                    <p className="text-xs opacity-70">{ev.date} {ev.time && `• ${ev.time}`}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Tipe Event</h3>
            <div className="space-y-1.5">
              {EVENT_TYPES.map(t => (
                <div key={t.value} className={`px-2 py-1 rounded text-xs border ${t.color}`}>{t.label}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Tambah Event" size="sm"
        footer={<><button onClick={() => setShowModal(false)} className="btn-secondary">Batal</button><button onClick={handleAddEvent} className="btn-primary">Tambah</button></>}
      >
        <div className="space-y-4">
          <Input label="Judul Event *" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Judul kegiatan" />
          <FormRow>
            <Input label="Tanggal *" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
            <Input label="Waktu" type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} />
          </FormRow>
          <Select label="Tipe Event" value={form.type} onChange={e => setForm({...form, type: e.target.value})} options={EVENT_TYPES.map(t => ({ value: t.value, label: t.label }))} />
        </div>
      </Modal>
    </div>
  );
}
